"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import { useParams, usePermission } from "@/hooks";
import {
  fetchAsaasSubaccountsOverview,
  createAsaasSubaccount,
  fetchAsaasSubaccountDetail,
  updateAsaasSubaccount,
  transferAsaasOwnership,
  deleteAsaasSubaccount,
  fetchAsaasWebhooks,
  activateAsaasWebhook,
  deleteAsaasWebhook,
  type OverviewQueryParams,
} from "@/store/modules/asaasAdmin/thunks";
import { forceClearOverviewLoading } from "@/store/modules/asaasAdmin/slice";
import type {
  AsaasSubaccountCreatePayload,
  AsaasSubaccountUpdatePayload,
  AsaasOwnershipTransferPayload,
} from "@/types";

const DEFAULT_SORT = { field: "created_at", direction: "desc" as const };

export const useAsaasAdmin = () => {
  const dispatch: AppDispatch = useDispatch();
  const { loading: loadingPermission } = usePermission();
  const { params, updateParams } = useParams();

  const { overview, details, webhooks, mutating } = useSelector(
    (s: RootState) => (s as any).asaasAdmin,
  );

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState(search);

  // debounce search 300ms
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  const isFetchingRef = useRef(false);
  const lastQuerySignatureRef = useRef<string | null>(null);

  // Fetch overview when filters change — simplified (no dedupe guards)
  useEffect(() => {
    const query: OverviewQueryParams = {
      search: debounced || undefined,
      sort: DEFAULT_SORT.field,
      direction: DEFAULT_SORT.direction,
      per_page: params.per_page,
      page: params.current_page,
      ...(params.filter as any),
    };

    // eslint-disable-next-line no-console
    dispatch(fetchAsaasSubaccountsOverview(query));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debounced,
    params.current_page,
    params.per_page,
    params.filter,
    loadingPermission,
    dispatch,
  ]);

  // Safety timeout: clear stuck loading after 12s
  useEffect(() => {
    if (overview.loading) {
      const id = setTimeout(() => {
        if (overview.loading) dispatch(forceClearOverviewLoading());
      }, 12000);
      return () => clearTimeout(id);
    }
  }, [overview.loading, dispatch]);

  const handlePage = (page: number) => updateParams({ current_page: page });
  const handlePerPage = (per: number) =>
    updateParams({ per_page: per, current_page: 1 });

  // CRUD wrappers
  const createSubaccount = useCallback(
    (payload: AsaasSubaccountCreatePayload) => {
      // sanitize cpf/cnpj digits only
      payload.cpfCnpj = payload.cpfCnpj.replace(/\D/g, "");
      // normalize mobile phone: strip country code 55 when present
      if (payload.mobilePhone) {
        const digits = payload.mobilePhone.replace(/\D/g, "");
        payload.mobilePhone =
          digits.startsWith("55") && digits.length > 11
            ? digits.slice(2)
            : digits;
      }
      return dispatch(createAsaasSubaccount(payload));
    },
    [dispatch],
  );

  const loadDetail = useCallback(
    (establishmentId: number | string) => {
      return dispatch(fetchAsaasSubaccountDetail(establishmentId));
    },
    [dispatch],
  );

  const updateSubaccount = useCallback(
    (establishmentId: number | string, data: AsaasSubaccountUpdatePayload) => {
      if (data.cpfCnpj) data.cpfCnpj = data.cpfCnpj.replace(/\D/g, "");
      return dispatch(updateAsaasSubaccount({ establishmentId, data }));
    },
    [dispatch],
  );

  const transferOwnership = useCallback(
    (establishmentId: number | string, data: AsaasOwnershipTransferPayload) => {
      data.new_owner_cpfCnpj = data.new_owner_cpfCnpj.replace(/\D/g, "");
      return dispatch(transferAsaasOwnership({ establishmentId, data }));
    },
    [dispatch],
  );

  const removeSubaccount = useCallback(
    (establishmentId: number | string) => {
      return dispatch(deleteAsaasSubaccount(establishmentId));
    },
    [dispatch],
  );

  const loadWebhooks = useCallback(
    (establishmentId: number | string) => {
      return dispatch(fetchAsaasWebhooks(establishmentId));
    },
    [dispatch],
  );

  const activateWebhook = useCallback(
    (establishmentId: number | string, url?: string) => {
      return dispatch(activateAsaasWebhook({ establishmentId, url }));
    },
    [dispatch],
  );

  const removeWebhook = useCallback(
    (establishmentId: number | string, webhookId: string | number) => {
      return dispatch(deleteAsaasWebhook({ establishmentId, webhookId }));
    },
    [dispatch],
  );

  const pagination = overview.pagination;
  const totalPages = pagination ? pagination.last_page : 1;

  return {
    search,
    setSearch,
    overview,
    details,
    webhooks,
    mutating,
    handlePage,
    handlePerPage,
    createSubaccount,
    loadDetail,
    updateSubaccount,
    transferOwnership,
    removeSubaccount,
    loadWebhooks,
    activateWebhook,
    removeWebhook,
    totalPages,
    currentPage: pagination?.current_page || 1,
    refresh: () => {
      const query: OverviewQueryParams = {
        search: debounced || undefined,
        sort: DEFAULT_SORT.field,
        direction: DEFAULT_SORT.direction,
        per_page: params.per_page,
        page: params.current_page,
        ...(params.filter as any),
      };
      return dispatch(fetchAsaasSubaccountsOverview(query));
    },
  };
};

export default useAsaasAdmin;
